import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "~/api/errors";
import { useApi } from "~/api/provider";
import type { ReasoningApi, ReasoningSession } from "~/api/reasoningTypes";
import {
  clearReasoningRecovery,
  readReasoningRecovery,
  saveReasoningRecovery,
} from "~/features/reasoning/recoveryStorage";
import { useReasoningSupport } from "~/features/reasoning/useReasoningSupport";
import { Button } from "~/foundation/components/Button";

type Upload = Omit<Parameters<ReasoningApi["retainArtifact"]>[0], "content"> & {
  sha256: string;
  bytes: number;
};
const recoveryKey = (id: string) => `docit:reasoning-artifact:1:${id}`;
const digest = async (bytes: Uint8Array<ArrayBuffer>) =>
  Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)), (x) =>
    x.toString(16).padStart(2, "0"),
  ).join("");
function readUpload(sessionId: string): Upload | null {
  try {
    const value = JSON.parse(
      readReasoningRecovery(recoveryKey(sessionId)) ?? "null",
    ) as Upload | null;
    return value?.sessionId === sessionId &&
      value.commandId &&
      /^[a-f0-9]{64}$/.test(value.sha256) &&
      Number.isInteger(value.expectedSessionRevision) &&
      value.bytes > 0
      ? value
      : null;
  } catch {
    return null;
  }
}

/** Recovery retains only command identity and a digest. File bytes stay out of browser storage. */
export function ReasoningArtifactsSection({
  session,
  busy,
  refresh,
  onBusyChange,
  onDirtyChange,
}: {
  session: ReasoningSession;
  busy: boolean;
  refresh: () => Promise<unknown>;
  onBusyChange: (busy: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const { limits } = useReasoningSupport();
  const [pending, setPending] = useState(() => readUpload(session.id));
  const [file, setFile] = useState<File | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => {
    onDirtyChange(!!file || !!pending);
  }, [file, pending, onDirtyChange]);
  useEffect(
    () => () => {
      onBusyChange(false);
      onDirtyChange(false);
    },
    [onBusyChange, onDirtyChange],
  );
  const retained = session.records.filter((row) => row.kind === "retained-artifact");
  if (!limits.maxRetainedArtifactBytes && !retained.length) return null;

  const upload = async () => {
    if (!file || busy || inFlight.current) return;
    inFlight.current = true;
    setWorking(true);
    onBusyChange(true);
    setError("");
    try {
      if (!file.size || file.size > limits.maxRetainedArtifactBytes)
        throw new Error(t("Choose a non-empty UTF-8 text file within the upload limit."));
      const bytes = new Uint8Array(await file.arrayBuffer());
      const sha256 = await digest(bytes);
      // Preserve a UTF-8 BOM and refuse malformed input instead of silently changing its bytes.
      const content = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes);
      if (content.includes("\0")) throw new Error(t("Text files cannot contain NUL characters."));
      if (pending && (pending.sha256 !== sha256 || pending.bytes !== bytes.length))
        throw new Error(t("Reselect the same file to retry the retained upload command."));
      const command: Upload = pending ?? {
        commandId: crypto.randomUUID(),
        sessionId: session.id,
        expectedSessionRevision: session.revision,
        sha256,
        bytes: bytes.length,
        mediaType: /\.json$/i.test(file.name)
          ? "application/json"
          : /\.md$/i.test(file.name)
            ? "text/markdown"
            : "text/plain",
      };
      const serialized = JSON.stringify(command);
      saveReasoningRecovery(recoveryKey(session.id), serialized);
      setPending(command);
      if (readReasoningRecovery(recoveryKey(session.id)) !== serialized)
        throw new Error(t("Enable browser storage to retain this upload's retry command."));
      const { sha256: _hash, bytes: _size, ...input } = command;
      await api.reasoning.retainArtifact({ ...input, content });
      clearReasoningRecovery(recoveryKey(session.id));
      setPending(null);
      setFile(null);
      if (picker.current) picker.current.value = "";
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      if (
        cause instanceof ApiError &&
        cause.status >= 400 &&
        cause.status < 500 &&
        ![408, 429].includes(cause.status)
      ) {
        clearReasoningRecovery(recoveryKey(session.id));
        setPending(null);
      }
    } finally {
      inFlight.current = false;
      setWorking(false);
      onBusyChange(false);
    }
  };
  const download = async (id: string) => {
    setError("");
    try {
      const artifact = await api.reasoning.readArtifact(session.id, id);
      const bytes = new TextEncoder().encode(artifact.content);
      const record = retained.find((row) => row.id === id)!;
      if (
        bytes.length !== artifact.bytes ||
        (await digest(bytes)) !== artifact.sha256 ||
        record.payload.sha256 !== artifact.sha256 ||
        record.payload.bytes !== artifact.bytes
      )
        throw new Error(t("The downloaded bytes do not match the retained artifact."));
      const url = URL.createObjectURL(new Blob([bytes], { type: artifact.mediaType }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `reasoning-${id}.${artifact.mediaType === "application/json" ? "json" : artifact.mediaType === "text/markdown" ? "md" : "txt"}`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };
  return (
    <section className="flex flex-col gap-3 rounded border border-divider p-3">
      <h3 className="font-medium">{t("Retained files")}</h3>
      <p>
        {t(
          "Keep full text alongside a candidate summary. Uploaded files remain unverified until reviewed.",
        )}
      </p>
      <p>
        {t("Maximum file size: {{size}} MiB", {
          size: limits.maxRetainedArtifactBytes / 1024 / 1024,
        })}
      </p>
      <input
        ref={picker}
        type="file"
        accept=".txt,.md,.json,text/plain,text/markdown,application/json"
        aria-label={t("Text artifact")}
        disabled={busy || working}
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
      />
      {pending && (
        <p role="status">{t("Reselect the same file to retry the retained upload command.")}</p>
      )}
      <Button disabled={busy || working || !file} onClick={() => void upload()}>
        {pending ? t("Retry retained upload") : t("Retain file")}
      </Button>
      {error && <p role="alert">{error}</p>}
      {retained.map((row) => (
        <div key={row.id} className="flex items-center gap-2">
          <span>
            {String(row.payload.mediaType)} · {String(row.payload.bytes)} {t("bytes")}
          </span>
          <Button disabled={busy || working || !row.eligible} onClick={() => void download(row.id)}>
            {t("Download retained file")}
          </Button>
        </div>
      ))}
    </section>
  );
}
